import { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Sparkles, ArrowRight, ArrowLeft, RotateCcw, Star, ShieldCheck } from "lucide-react";
import { http, formatINR } from "@/lib/api";
import { Link } from "react-router-dom";
import { useCart } from "@/context/CartContext";

const QUESTIONS = [
  {
    q: "How would you describe your body frame?",
    options: [
      { label: "Thin, lean, hard to gain weight", d: "vata" },
      { label: "Medium build, athletic, moderate muscle", d: "pitta" },
      { label: "Solid, broad, gains weight easily", d: "kapha" },
    ],
  },
  {
    q: "How does your skin usually behave?",
    options: [
      { label: "Dry, thin, cool to touch", d: "vata" },
      { label: "Warm, reddish, prone to sensitivity", d: "pitta" },
      { label: "Smooth, oily, thick with good moisture", d: "kapha" },
    ],
  },
  {
    q: "How is your energy pattern through the day?",
    options: [
      { label: "Comes in bursts, then I crash", d: "vata" },
      { label: "Intense and focused, I push hard", d: "pitta" },
      { label: "Steady and enduring, slow to start", d: "kapha" },
    ],
  },
  {
    q: "How do you usually sleep?",
    options: [
      { label: "Light, interrupted, restless mind", d: "vata" },
      { label: "Moderate but wake up sharp", d: "pitta" },
      { label: "Deep and long, hard to wake up", d: "kapha" },
    ],
  },
  {
    q: "How do you react under stress?",
    options: [
      { label: "Anxious, worried, over-thinking", d: "vata" },
      { label: "Irritable, sharp, quick to anger", d: "pitta" },
      { label: "Withdrawn, avoid, comfort-eat", d: "kapha" },
    ],
  },
  {
    q: "Which weather do you enjoy the most?",
    options: [
      { label: "Warm, humid — I dislike cold & wind", d: "vata" },
      { label: "Cool, breezy — I overheat easily", d: "pitta" },
      { label: "Warm and dry — damp cold feels heavy", d: "kapha" },
    ],
  },
  {
    q: "How is your digestion typically?",
    options: [
      { label: "Variable, occasional bloating or gas", d: "vata" },
      { label: "Strong appetite, gets acidic if delayed", d: "pitta" },
      { label: "Slow, feel heavy after meals", d: "kapha" },
    ],
  },
  {
    q: "How do you speak and communicate?",
    options: [
      { label: "Fast, expressive, jumps between ideas", d: "vata" },
      { label: "Sharp, precise, persuasive", d: "pitta" },
      { label: "Calm, slow, measured", d: "kapha" },
    ],
  },
];

const DOSHA_META = {
  vata: {
    name: "Vata",
    tagline: "Air & Space · Creative and quick",
    color: "text-sky-800 bg-sky-50 border-sky-200",
    accent: "bg-sky-600",
    note: "Vata types benefit from warm, grounding, and nourishing formulations. I recommend Ashwagandha for the nervous system, Brahmi Ghrita for focus, and warm sesame-based abhyanga. Favour warm meals, oil massage, and steady routines.",
    picks: ["Ashwagandha", "Brahmi", "Sesame", "Bhringraj", "Golden Milk"],
  },
  pitta: {
    name: "Pitta",
    tagline: "Fire & Water · Focused and driven",
    color: "text-amber-900 bg-amber-50 border-amber-200",
    accent: "bg-amber-600",
    note: "Pitta types thrive with cooling, hydrating, and calming herbs. I suggest Shatavari for hormonal balance, Rose & Sandalwood serums for skin, and Amla for internal cooling. Favour cool foods, moonlight walks, and mindful pauses.",
    picks: ["Shatavari", "Kumkumadi", "Rose", "Amla", "Sandalwood"],
  },
  kapha: {
    name: "Kapha",
    tagline: "Earth & Water · Grounded and steady",
    color: "text-emerald-900 bg-emerald-50 border-emerald-200",
    accent: "bg-emerald-600",
    note: "Kapha types flourish with warming, stimulating, and drying herbs. Try Triphala for gentle detox, Tulsi & Ginger tea for circulation, and neem-turmeric cleansers. Favour movement, sunrise routines, and lighter, spiced foods.",
    picks: ["Triphala", "Tulsi", "Neem", "Turmeric", "Chyawanprash"],
  },
  tridoshic: {
    name: "Tridoshic",
    tagline: "Balanced blend of all three",
    color: "text-purple-900 bg-purple-50 border-purple-200",
    accent: "bg-purple-600",
    note: "You show a balanced constitution across all three doshas — rare and beautiful. Focus on seasonal balance: warm and grounding in winter, cooling in summer, and detoxifying in transitions. Rasayana tonics like Chyawanprash suit you year-round.",
    picks: ["Chyawanprash", "Triphala", "Amla", "Ashwagandha", "Golden Milk"],
  },
};

function computeDosha(votes) {
  const entries = Object.entries(votes).sort((a, b) => b[1] - a[1]);
  const [top, topCount] = entries[0];
  const [, secondCount] = entries[1] || [null, 0];
  if (topCount - secondCount <= 1) return "tridoshic";
  return top;
}

export default function DoshaQuiz({ open, onClose }) {
  const [step, setStep] = useState(0);
  const [answers, setAnswers] = useState([]);
  const [products, setProducts] = useState([]);
  const [dominant, setDominant] = useState(null);
  const { add } = useCart();

  const total = QUESTIONS.length;
  const done = step >= total;
  const percent = done ? 100 : Math.round((step / total) * 100);

  useEffect(() => {
    if (!open) {
      setStep(0); setAnswers([]); setProducts([]); setDominant(null);
    }
  }, [open]);

  useEffect(() => {
    if (!done || dominant) return;
    const votes = { vata: 0, pitta: 0, kapha: 0 };
    answers.forEach((a) => { votes[a] = (votes[a] || 0) + 1; });
    const d = computeDosha(votes);
    setDominant(d);
    // Recommend products for that dosha (fallback to tridoshic if empty)
    http.get("/products", { params: { dosha: d } }).then((r) => {
      if (r.data.length >= 3) setProducts(r.data.slice(0, 4));
      else http.get("/products").then((all) => setProducts(all.data.slice(0, 4)));
    });
  }, [done, answers, dominant]);

  const answer = (dosha) => {
    setAnswers([...answers, dosha]);
    setStep(step + 1);
  };
  const back = () => {
    if (step === 0) return;
    setAnswers(answers.slice(0, -1));
    setStep(step - 1);
    setDominant(null);
  };
  const restart = () => { setStep(0); setAnswers([]); setDominant(null); setProducts([]); };

  const meta = dominant ? DOSHA_META[dominant] : null;

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto bg-[#FAF8F5] border border-[#E2DDD5]" data-testid="dosha-quiz-modal">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl font-bold text-[#1E3A2B] flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-[#D97706]" /> Dosha Discovery Quiz
          </DialogTitle>
        </DialogHeader>

        {!done && (
          <div className="space-y-6" data-testid="dosha-quiz-question">
            <div>
              <div className="flex items-center justify-between text-xs font-mono uppercase text-[#5A6B5D]">
                <span>Question {step + 1} of {total}</span>
                <span>{percent}%</span>
              </div>
              <div className="mt-2 h-1.5 bg-[#E2DDD5] rounded-full overflow-hidden">
                <div className="h-full bg-[#1E3A2B] transition-all duration-500" style={{ width: `${percent}%` }} />
              </div>
            </div>

            <h3 className="font-serif text-2xl text-[#1A201C] leading-snug">{QUESTIONS[step].q}</h3>

            <div className="space-y-3">
              {QUESTIONS[step].options.map((opt, i) => (
                <button
                  key={i}
                  onClick={() => answer(opt.d)}
                  data-testid={`dosha-answer-${i}`}
                  className="w-full text-left p-4 rounded-2xl bg-white border border-[#E2DDD5] hover:border-[#1E3A2B] hover:shadow-md transition-all group flex items-center justify-between gap-3"
                >
                  <span className="text-sm text-[#1A201C]">{opt.label}</span>
                  <ArrowRight className="w-4 h-4 text-[#5A6B5D] group-hover:text-[#1E3A2B] group-hover:translate-x-1 transition-all" />
                </button>
              ))}
            </div>

            <div className="flex justify-between items-center pt-2">
              <button onClick={back} disabled={step === 0} className="text-sm text-[#5A6B5D] disabled:opacity-40 inline-flex items-center gap-1" data-testid="dosha-back-button">
                <ArrowLeft className="w-4 h-4" /> Back
              </button>
              <span className="text-[10px] font-mono text-[#5A6B5D]">Guided by Dr. Rupali Srivastava</span>
            </div>
          </div>
        )}

        {done && meta && (
          <div className="space-y-6" data-testid="dosha-quiz-result">
            <div className="text-center">
              <div className={`inline-flex items-center gap-1.5 px-4 py-1.5 rounded-full text-xs font-mono uppercase tracking-wider border ${meta.color}`}>
                Your dominant dosha
              </div>
              <h2 className="mt-3 font-serif text-5xl font-bold text-[#1A201C]">{meta.name}</h2>
              <p className="mt-1 text-sm text-[#5A6B5D]">{meta.tagline}</p>
            </div>

            <div className="bg-white border border-[#E2DDD5] rounded-2xl p-5 relative overflow-hidden">
              <div className={`absolute top-0 left-0 w-1 h-full ${meta.accent}`} />
              <div className="flex items-start gap-3">
                <div className="w-10 h-10 rounded-full bg-[#E8F0EA] flex items-center justify-center shrink-0">
                  <ShieldCheck className="w-5 h-5 text-[#1E3A2B]" />
                </div>
                <div>
                  <div className="text-xs font-mono uppercase tracking-wider text-[#D97706]">Doctor's note</div>
                  <div className="font-semibold text-[#1A201C] mt-0.5">Dr. Rupali Srivastava · Founder, Amiroop Healthion LLP</div>
                  <p className="mt-2 text-sm text-[#1A201C] leading-relaxed italic">"{meta.note}"</p>
                </div>
              </div>
            </div>

            {products.length > 0 && (
              <div>
                <div className="text-xs font-mono uppercase text-[#5A6B5D]">Personalised picks</div>
                <h3 className="font-serif text-xl font-semibold text-[#1E3A2B] mt-1">Recommended for your {meta.name} constitution</h3>
                <div className="mt-4 grid grid-cols-2 gap-3">
                  {products.map((p) => (
                    <div key={p.id} className="bg-white border border-[#E2DDD5] rounded-2xl overflow-hidden" data-testid={`dosha-recommendation-${p.id}`}>
                      <Link to={`/product/${p.id}`} onClick={onClose} className="block aspect-square overflow-hidden bg-[#E8F0EA]">
                        <img src={p.image_url} alt={p.name} className="w-full h-full object-cover hover:scale-105 transition-transform duration-500" />
                      </Link>
                      <div className="p-3">
                        <Link to={`/product/${p.id}`} onClick={onClose}>
                          <div className="text-sm font-semibold text-[#1A201C] line-clamp-2 leading-snug hover:text-[#1E3A2B]">{p.name}</div>
                        </Link>
                        <div className="mt-1 flex items-center gap-1 text-[10px] text-[#5A6B5D] font-mono">
                          <Star className="w-3 h-3 fill-[#D97706] text-[#D97706]" /> {p.rating || "New"}
                          <span>· {p.vendor_name}</span>
                        </div>
                        <div className="mt-2 flex items-center justify-between">
                          <div className="font-serif text-base font-bold text-[#1E3A2B]">{formatINR(p.price)}</div>
                          <button
                            onClick={() => add(p)}
                            className="text-[10px] font-semibold bg-[#1E3A2B] text-white px-3 py-1.5 rounded-full hover:bg-[#D97706] transition-colors"
                            data-testid={`dosha-add-${p.id}`}
                          >
                            Add to cart
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-wrap gap-3 justify-between border-t border-[#E2DDD5] pt-4">
              <button onClick={restart} className="text-sm text-[#5A6B5D] inline-flex items-center gap-1" data-testid="dosha-restart-button">
                <RotateCcw className="w-4 h-4" /> Retake quiz
              </button>
              <Link
                to={`/products?dosha=${dominant === "tridoshic" ? "tridoshic" : dominant}`}
                onClick={onClose}
                className="btn-forest text-sm inline-flex items-center gap-1"
                data-testid="dosha-see-all-button"
              >
                See all {meta.name} products <ArrowRight className="w-4 h-4" />
              </Link>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
